import React from 'react';
import styled from 'styled-components';
import SelectBox from '../../shared/component/SelectBox';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue, MemberTitleInput } from '../../shared/component/headless/input/InputBody';
import { getMemberNamePlaceholder, getPageMessage } from '../../shared/constants/pageMessages';
import {
  AboutContentMode,
  CountryType,
  DeviceType,
  SelectType,
  TextAlignment,
} from '../../shared/enum/EnumRepository';
import { useMemberInfoStore } from '../../shared/store/memberInfoStore';
import { useAboutViewStore } from '../../shared/store/aboutViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useValidation } from '../../shared/hooks/useValidation';
import { MemberInfoData } from '../../shared/dto/EntityRepository';

/** Country가 NONE이면 (b. year)만, year가 비면 (country)만, 둘 다 있으면 (b. year, country). 빈 문자열·"0" 등은 Number가 0이라 (b. 미표시) */
function formatInlineMemberInfo(year: string, country: CountryType): string {
  const hasYear = Number(year) !== 0;
  if (country === CountryType.NONE) {
    return hasYear ? `(b. ${year})` : '';
  }
  if (!hasYear) {
    return `(${country})`;
  }
  return `(b. ${year}, ${country})`;
}

const MemberProfileAndInfo: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const memberInfo = useMemberInfoStore((s) => s.memberInfo);
  const updatMemberInfo = useMemberInfoStore((s) => s.updatMemberInfo);
  const aboutContentMode = useAboutViewStore((s) => s.aboutContentMode);
  const { checkType } = useValidation();

  const handleChangeWithValidate = (updates: Partial<MemberInfoData>) => {
    for (const key in updates) {
      const field = key as keyof MemberInfoData;
      const value = updates[field];
      if (typeof value === 'string') {
        const stringValue = value satisfies string;
        if (!checkType(field, stringValue)) {
          return;
        }
      }
    }
    updatMemberInfo(updates);
  };

  if (!memberInfo) {
    return null;
  }

  const { name, nameEn, year, country } = memberInfo;
  const isAboutEng = aboutContentMode === AboutContentMode.ENG;
  const nameFieldValue = isAboutEng ? nameEn : name;
  const showNameDefaultLabel = !String(nameFieldValue ?? '').trim();
  const nameDefaultLabel = getPageMessage('MEMBER_NAME_DEFAULT');
  const inlineMemberInfo = formatInlineMemberInfo(year, country);

  return (
    <ProfileAndInfo>
      {isEditMode ? (
        <>
          <NameFieldRow $isMobile={isMobile}>
            <MemberNameInputWrap>
              <HeadlessInput
                key={isAboutEng ? 'en' : 'orig'}
                type="text"
                value={nameFieldValue}
                placeholder={getMemberNamePlaceholder()}
                handleChange={(e) =>
                  handleChangeWithValidate(
                    isAboutEng ? { nameEn: e.target.value } : { name: e.target.value },
                  )
                }
                StyledInput={MemberTitleInput}
                $alignment={TextAlignment.LEFT}
              />
            </MemberNameInputWrap>
          </NameFieldRow>
          <BornFieldRow>
            <BornFieldLabel>Born</BornFieldLabel>
            <HeadlessInput
              type="text"
              value={year}
              placeholder="0000"
              handleChange={(e) => handleChangeWithValidate({ year: e.target.value })}
              StyledInput={MemberInfoValue}
            />
          </BornFieldRow>
          <BornFieldRow>
            <BornFieldLabel>Country</BornFieldLabel>
            <CountrySelectBoxWrapper>
              <SelectBox
                value={country}
                selectType={SelectType.COUNTRY}
                handleChange={(value: CountryType) => handleChangeWithValidate({ country: value })}
                direction={false}
              />
            </CountrySelectBoxWrapper>
          </BornFieldRow>
        </>
      ) : (
        <NameTitleValueSection>
          {showNameDefaultLabel ? (
            <NameTitleDefaultDiv>{nameDefaultLabel}</NameTitleDefaultDiv>
          ) : (
            <NameTitleValue>{nameFieldValue}</NameTitleValue>
          )}
          <InlineMemberInfo>{inlineMemberInfo}</InlineMemberInfo>
        </NameTitleValueSection>
      )}
    </ProfileAndInfo>
  );
};

const ProfileAndInfo = styled.div`
  display: flex;
  gap: clamp(8px, 2vw, 10px);
  width: fit-content;
  height: fit-content;
  flex-direction: column;
  margin-bottom: clamp(8px, 2vw, 16px);
`;

const NameTitleValue = styled.h1`
  ${({ theme }) => theme.typography.Body_02_1};
`;

const InlineMemberInfo = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Footer_01};
`;

const NameFieldRow = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '8px' : '10px')};
`;

const MemberNameInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameTitleValueSection = styled.div`
  width: 100%;
  padding: 5px 0px;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  ${({ theme }) => theme.typography.Body_01_1};
`;

const NameTitleDefaultDiv = styled.div`
  width: fit-content;
  ${({ theme }) => theme.typography.Body_02_1};
  color: ${({ theme }) => theme.colors.color_Gray_05};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const BornFieldRow = styled.div`
  display: flex;
  height: 20px;
`;

const BornFieldLabel = styled.div`
  width: clamp(100px, 10vw, 160px);
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const CountrySelectBoxWrapper = styled.article`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

export default MemberProfileAndInfo;
